import { useEffect, useState } from "react";
import { Delete } from "lucide-react";
import { playLetter } from "../lib/api";

// 3-row QWERTY-ish for familiarity; can be swapped to alphabetical if preferred.
const ROWS = [
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["z", "x", "c", "v", "b", "n", "m"],
];

export default function OnScreenKeyboard({ value, onChange, onEnter, disabled }) {
  const [flash, setFlash] = useState(null); // letter currently "pressed"

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 220);
    return () => clearTimeout(t);
  }, [flash]);

  const tap = async (L) => {
    if (disabled) return;
    setFlash(L);
    onChange?.(((value ?? "") + L).slice(0, 24));
    try { await playLetter(L); } catch (err) { console.warn("[keyboard.tap] letter audio failed:", err); }
  };

  const backspace = () => {
    if (disabled) return;
    onChange?.((value ?? "").slice(0, -1));
  };

  return (
    <div className="mt-5 select-none" data-testid="onscreen-keyboard">
      {ROWS.map((row) => (
        <div key={row.join("")} className={`flex justify-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 ${row.length === 9 ? "px-3" : ""}`}>
          {row.map((L) => {
            const pressed = flash === L;
            return (
              <button
                key={L}
                type="button"
                onClick={() => tap(L)}
                disabled={disabled}
                data-testid={`key-${L}`}
                className={`font-display font-bold text-[#1E1B4B] rounded-xl sm:rounded-2xl border-2 border-blue-200 bg-white w-[9vw] max-w-[48px] sm:w-12 sm:h-12 h-10 text-lg sm:text-xl transition-all shadow-[0_3px_0_#CBD5E1] active:translate-y-[2px] active:shadow-[0_1px_0_#CBD5E1] disabled:opacity-40 ${
                  pressed ? "bg-[#FBBF24] border-transparent shadow-[0_2px_0_#B45309] translate-y-[1px]" : "hover:bg-blue-50"
                }`}
              >
                {L.toUpperCase()}
              </button>
            );
          })}
        </div>
      ))}
      <div className="flex justify-center gap-2 mt-1 sm:mt-2">
        <button
          type="button"
          onClick={backspace}
          disabled={disabled}
          data-testid="key-backspace"
          className="inline-flex items-center gap-2 px-5 h-11 sm:h-12 rounded-2xl border-2 border-rose-200 bg-white text-[#F43F5E] font-bold shadow-[0_3px_0_#FECDD3] active:translate-y-[2px] active:shadow-[0_1px_0_#FECDD3] disabled:opacity-40"
          title="Backspace"
        >
          <Delete className="w-5 h-5" strokeWidth={3} /> Back
        </button>
        <button
          type="button"
          onClick={() => onEnter?.()}
          disabled={disabled}
          data-testid="key-enter"
          className="px-6 h-11 sm:h-12 rounded-2xl bg-[#10B981] text-white font-bold shadow-[0_3px_0_#047857] active:translate-y-[2px] active:shadow-[0_1px_0_#047857] disabled:opacity-40"
        >
          Enter
        </button>
      </div>
    </div>
  );
}
